/**
 * useSupportSessionsAudit.ts
 * ----------------------------------------------------------------------------
 * Hooks de auditoria das sessões de suporte WL.
 *
 * Backend (RPCs SECURITY DEFINER):
 *   - wl_list_support_sessions / wl_get_support_session_details
 *     (painel /wl, valida wl_assert_permission('wl.audit','view'))
 *   - admin_list_support_sessions / admin_get_support_session_details
 *     (painel /admin, valida root_admin)
 *
 * Cada lista retorna `total_count` (janelado) para paginação server-side.
 * Detalhes incluem o array `actions` (JSONB) com as ações registradas em
 * wl_audit_log durante a janela [started_at, ended_at|now].
 *
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

// ---------- Tipos ----------
export type SupportSessionStatus = "active" | "ended" | "expired";

export interface SupportSessionRow {
  session_id: string;
  tenant_id: string;
  tenant_name: string | null;
  tenant_slug: string | null;
  operator_user_id: string;
  operator_display_name: string | null;
  operator_email: string | null;
  reason: string | null;
  started_at: string;
  expires_at: string;
  ended_at: string | null;
  ended_reason: string | null;
  status: SupportSessionStatus;
  duration_seconds: number;
  provisioned_member: boolean | null;
  provisioned_role: boolean | null;
  actions_count: number;
  total_count: number;
}

export interface SupportSessionAction {
  id: string;
  menu_key: string | null;
  action: string;
  target_entity: string | null;
  target_entity_id: string | null;
  created_at: string;
}

export interface SupportSessionDetails extends Omit<SupportSessionRow, "total_count" | "actions_count"> {
  previous_tenant_id: string | null;
  actions: SupportSessionAction[];
}

export interface SupportSessionsFilters {
  tenantId?: string | null;
  operatorUserId?: string | null;
  status?: SupportSessionStatus | null;
  startedFrom?: string | null;
  startedTo?: string | null;
  search?: string | null;
  limit?: number;
  offset?: number;
}

// ---------- Builder de params ----------
function toRpcParams(f: SupportSessionsFilters) {
  return {
    p_tenant_id: f.tenantId ?? null,
    p_operator_user_id: f.operatorUserId ?? null,
    p_status: f.status ?? null,
    p_started_from: f.startedFrom ?? null,
    p_started_to: f.startedTo ?? null,
    p_search: f.search?.trim() ? f.search.trim() : null,
    p_limit: f.limit ?? 50,
    p_offset: f.offset ?? 0,
  };
}

// =============================================================================
// Painel WL
// =============================================================================
export function useWlSupportSessions(filters: SupportSessionsFilters, enabled = true) {
  return useQuery({
    queryKey: ["wl-support-sessions", filters],
    queryFn: async (): Promise<SupportSessionRow[]> => {
      const { data, error } = await supabase.rpc(
        "wl_list_support_sessions" as never,
        toRpcParams(filters) as never,
      );
      if (error) {
        logger.error("[useWlSupportSessions]", error.message);
        throw error;
      }
      return (data ?? []) as SupportSessionRow[];
    },
    enabled,
    staleTime: 15_000,
  });
}

export function useWlSupportSessionDetails(sessionId: string | null) {
  return useQuery({
    queryKey: ["wl-support-session-details", sessionId],
    queryFn: async (): Promise<SupportSessionDetails | null> => {
      if (!sessionId) return null;
      const { data, error } = await supabase
        .rpc("wl_get_support_session_details" as never, { p_session_id: sessionId } as never)
        .maybeSingle();
      if (error) {
        logger.error("[useWlSupportSessionDetails]", error.message);
        throw error;
      }
      return (data as SupportSessionDetails | null) ?? null;
    },
    enabled: !!sessionId,
  });
}

// =============================================================================
// Painel Admin Root
// =============================================================================
export function useAdminSupportSessions(filters: SupportSessionsFilters, enabled = true) {
  return useQuery({
    queryKey: ["admin-support-sessions", filters],
    queryFn: async (): Promise<SupportSessionRow[]> => {
      const { data, error } = await supabase.rpc(
        "admin_list_support_sessions" as never,
        toRpcParams(filters) as never,
      );
      if (error) {
        logger.error("[useAdminSupportSessions]", error.message);
        throw error;
      }
      return (data ?? []) as SupportSessionRow[];
    },
    enabled,
    staleTime: 15_000,
  });
}

export function useAdminSupportSessionDetails(sessionId: string | null) {
  return useQuery({
    queryKey: ["admin-support-session-details", sessionId],
    queryFn: async (): Promise<SupportSessionDetails | null> => {
      if (!sessionId) return null;
      const { data, error } = await supabase
        .rpc("admin_get_support_session_details" as never, { p_session_id: sessionId } as never)
        .maybeSingle();
      if (error) {
        logger.error("[useAdminSupportSessionDetails]", error.message);
        throw error;
      }
      return (data as SupportSessionDetails | null) ?? null;
    },
    enabled: !!sessionId,
  });
}
